import React from 'react';
import { Check, CheckIcon } from 'lucide-react';
import { checkHasPaymentMethod, checkSubscriptionStatus, createSubscription } from '../utils/api';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useSubscriberStatus from '../hooks/useSubscriberStatus';
import { isAuthed } from '../services/auth.service';

const PriceCard = ({
  title,
  price,
  description,
  features,
  isPopular = false,
  buttonText = "Get Started",
  className,
  show_buttons=true,
}) => {

  const [hasPaymentMethod, setHasPaymentMethod] = React.useState(false);
  const [hasSubscription, setHasSubscription] = React.useState(false);
  const [loadedPaymentMethod, setLoadedPaymentMethod] = React.useState(false);
  const [loadedSubscriptionCheck, setLoadedSubscriptionCheck] = React.useState(false);
  const [showConfirm, setShowConfirm] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [completed, setCompleted] = React.useState(false);
  const [selectedPlan, setSelectedPlan] = React.useState('');
  const { subscriberStatus, subscriberLoading, subscriberData, subscriberError, refetch } = useSubscriberStatus();
  const [currentPlan, setCurrentPlan] = React.useState('');
  const navigate = useNavigate();

  useEffect(() => {
    if (subscriberLoading == false && subscriberData != null && subscriberData.length > 0) {
      setCurrentPlan(subscriberData[0].name);
    }
  }, [subscriberLoading, subscriberData]);

  useEffect(() => {
    async function loadPaymentCheck() {
      let res = await checkHasPaymentMethod();

      if (res.success) {
        setHasPaymentMethod(res.data);
        setLoadedPaymentMethod(true);
      }
    }

    async function loadSubscriptionCheck() {
      let res = await checkSubscriptionStatus();

      if (res.success) {
        console.log('res', res.data);
        setHasSubscription(res.data);
        setLoadedSubscriptionCheck(true);
      }
    }

    loadPaymentCheck();
    loadSubscriptionCheck();
  }, []);

  async function handleUpgrade(name) {
    setSelectedPlan(name);
    if (loadedPaymentMethod == true) {
      if (hasPaymentMethod == false) {
        navigate('/account/payment-method');
      }

      else {
        setShowConfirm(true);
      }
    }
  }

  async function handleConfirm() {
    setSubmitting(true);
    let res = await createSubscription(selectedPlan);

    if (res.success) {
      setSubmitting(false);
      setCompleted(true);
    }

    else {
      alert(res.message);
    }
  }

  async function handleRedirect() {
    navigate('/dashboard/performance');
  }

  return (
    <div className={`relative rounded-2xl border bg-white shadow-sm transition-all ${isPopular ? " border-purple-600 " : " border-gray-200"} ${className}`
    }>
      {isPopular && (
        <div className="absolute -top-4 left-0 right-0 flex justify-center">
          <div className="bg-gradient-to-r from-amber-400 to-amber-600 text-white px-4 py-1 rounded-full text-sm font-medium">
            Most Popular
          </div>
        </div>
      )}

      <div className="p-6 pt-8">
        <h3 className="text-xl font-bold text-gray-900">{title}</h3>

        <div className="mt-4 flex items-baseline">
          <span className="text-4xl font-extrabold text-gray-900">{price}</span>
          <span className="ml-1 text-xl font-semibold text-gray-500">/month</span>
        </div>

        <p className="mt-4 text-gray-600">{description}</p>

        <ul className="mt-6 space-y-4">
          {features.map((feature, index) => (
            <li key={index} className="flex">
              <div className="flex-shrink-0">
                <Check className="h-5 w-5 text-green-500" />
              </div>
              <span className="ml-3 text-gray-600">{feature}</span>
            </li>
          ))}
        </ul>

        <div className="mt-8">
          {title == 'Starter' && show_buttons == true && (
            <>
              {isAuthed() == false && (
                <>
                  <button
                    className={`w-full p-2 rounded ${isPopular ? "button-gradient" : "bg-gray-100 text-gray-900  hover:bg-gray-200"}`}
                    onClick={() => navigate('/join-now')}
                  >
                    Get Started for Free
                  </button>
                </>
              )}

              {isAuthed() == true && (
                <> 
                  {hasSubscription == false && (
                    <>
                      <div className='text-gray-500 center'><CheckIcon /> You are already on this plan</div>
                    </>
                  )}
                </>
              )}
            </>
          )}

          {title == 'Professional' && show_buttons == true && (
            <>
              {isAuthed() == false && (
                <>
                  <button
                    className={`w-full p-2 rounded ${isPopular ? "button-gradient" : "bg-gray-100 text-gray-900  hover:bg-gray-200"}`}
                    onClick={() => navigate('/join-now')}
                  >
                    {buttonText}
                  </button>
                </>
              )}

              {isAuthed() == true && (
                <>
                  {currentPlan == 'Professional' && (
                    <>
                      <div className='text-gray-500 center'><CheckIcon /> You are already on this plan</div>
                    </>
                  )}

                  {hasSubscription == true && currentPlan != 'Professional' && (
                    <>
                     <i>
                        Cancel your current plan to upgrade to <span className='bg-clip-text bg-gradient-to-tl from-blue-600 to-violet-600 text-transparent font-bold'>Professional</span>.
                      </i>
                    </>
                  )}

                  {hasSubscription == false && (
                    <button
                      className={`w-full p-2 rounded ${isPopular ? "button-gradient" : "bg-gray-100 text-gray-900  hover:bg-gray-200"}`}
                      onClick={() => handleUpgrade('Professional')}
                    >
                      Upgrade
                    </button>
                  )}
                </>
              )}
            </>
          )}

          {title == 'Enterprise' && show_buttons== true && (
            <>
              {isAuthed() == false && (
                <>
                  <button
                    className={`w-full p-2 rounded ${isPopular ? "button-gradient" : "bg-gray-100 text-gray-900  hover:bg-gray-200"}`}
                    onClick={() => navigate('/join-now')}
                  >
                    {buttonText}
                  </button>
                </>
              )}

              {isAuthed() == true && (
                <>
                  {currentPlan == 'Enterprise' && (
                    <>
                      <span className='text-gray-500'>You are already on this plan</span>
                    </>
                  )}

                  {(currentPlan == 'Professional' || currentPlan == 'Starter') && (
                    <>
                      <i>
                        Cancel your current plan to upgrade to <span className='bg-clip-text bg-gradient-to-tl from-blue-600 to-violet-600 text-transparent font-bold'>Enterprise</span>.
                      </i>
                    </>
                  )}

                  {hasSubscription == false && (
                    <button
                      className={`w-full p-2 rounded ${isPopular ? "button-gradient" : "bg-gray-100 text-gray-900  hover:bg-gray-200"}`}
                      onClick={() => handleUpgrade(title)}
                    >
                      Upgrade
                    </button>
                  )}
                </>
              )}
            </>
          )}

        </div>
      </div>

      {showConfirm && (
        <>
          <div className='overlay'></div>
          <div className='modal center'>
            <div className='modal__close' onClick={() => setShowConfirm(false)}>
            </div>

            {completed == false && (
              <>
                <div className='center' style={{ fontSize: '70px' }}>
                </div>

                <h2 className='modal__h2'>UPGRADE TO <span className='highlight'>
                  {title}
                </span></h2>

                {/* <div className='upgrade__plan-price'>
                  <span className='upgrade__plan-price-amount'>

                  </span> / month
                </div> */}

                <div className='mt-20 center'>
                  {submitting == false && (
                    <button className='w-full bg-indigo-500 text-white shadow-sm hover:bg-indigo-400 focus-visible:outline-indigo-500 mt-8 block rounded-md px-3.5 py-2.5 text-center text-sm font-semibold focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 sm:mt-10' onClick={() => handleConfirm()}>CONFIRM SUBSCRIPTION</button>
                  )}

                  {submitting == true && (
                    <div>
                      <svg className="animate-spin h-5 w-5 mr-3 text-white" viewBox="0 0 24 24"></svg>
                      Processing...
                    </div>
                  )}
                </div>

                <div className='upgrade__disclosure mt-8'>
                  By clicking 'confirm', you agree to be charged the monthly subscription fee which will automatically be charged each month. You can cancel anytime.
                </div>

                <div className='center'>
                  <span className='link' onClick={() => setShowConfirm(false)}>Cancel</span>
                </div>
              </>
            )}

            {completed == true && (
              <>
                <div>
                  <div className='center' style={{ fontSize: '70px' }}>
                  </div>

                  <h2 className='modal__h2 mt-20'><span className='highlight'>SUCCESS!</span></h2>

                  <p className='mt-20'>
                    You are now on the plan and instantly get access to all journeys and interviews.
                  </p>

                  <div className='mt-20'>
                    <button className='w-full bg-indigo-500 text-white shadow-sm hover:bg-indigo-400 focus-visible:outline-indigo-500 mt-8 block rounded-md px-3.5 py-2.5 text-center text-sm font-semibold focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 sm:mt-10' onClick={() => handleRedirect()}>TAKE ME TO MY DASHBOARD</button>
                  </div>
                </div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default PriceCard;
